import React from 'react';
import SegmentedToggle from './SegmentedToggle';
import './PageViews.css';

/* LES COMMANDES DE PORTÉE PAGE (gabarit validé le 2026-09-17) — la rangée qui
   se pose SOUS LE TITRE, alignée à gauche (2026-09-18).

   Elle a fermé la ligne du titre une journée. La bande de contexte l'y ayant
   rejointe, la ligne débordait dès trois commandes et la bande retombait seule
   sur un second rang, à droite : deux lignes mal appariées. La ligne du titre
   garde donc le CONTEXTE, court et stable (statut du réel, exercice, unité,
   sortie) ; les commandes, plus longues et variables, prennent le rang du
   dessous, où elles commencent au même x que le titre et que les cartes.
   Le libellé se pose AU-DESSUS de sa commande (2026-09-18) : devant elle, sa
   largeur variable décalait chaque groupe d'autant, si bien qu'aucun ne
   commençait au même x, ni entre eux, ni avec le titre juste au-dessus.

   Le désordre qu'elle range : la même nature de commande vivait tantôt sur la
   ligne de titre, tantôt dans l'entête d'une carte, tantôt dans sa barre
   d'outils, sans règle. L'échelle est désormais une échelle de PORTÉE :

     1. Barre supérieure — où je suis (client, modules, accès)
     2. Bande de page    — quand je lis (exercice), comment je compte (unité),
                           comment je sors (export)
     3. CETTE RANGÉE      — ce que je lis : elle pilote TOUTE la page
     4. Entête de carte   — comment CETTE carte se représente
     5. Barre d'outils    — ce que cette carte montre (filtres, recherche)

   Le test pour trancher : « si j'ajoute une seconde carte à la page, cette
   commande doit-elle la piloter aussi ? » Oui, c'est ici. Non, c'est la carte.
   Et si elle RÉDUIT des lignes au lieu de changer un calcul, c'est la barre
   d'outils, jamais ici.

   Une page DÉCLARE ses commandes à son gabarit (`PageShell views=`), elle ne
   les pose jamais à la main : c'est ce qui garantit la place, l'ordre et le
   costume — le même patron que l'unité et les exports.

   Deux formes seulement, et toutes deux portent le COSTUME DE LA CHARTE :
     • `segmented` — deux ou trois choix qu'on change souvent (la BASE de
       lecture : Réel | Prévisionnel | Budget). Rendue par `SegmentedToggle`
       en taille large : groupe blanc à filet, choix retenu en aplat d'accent
       à texte blanc. *(Un costume maison a vécu une heure, blanc sur beige
       très clair : les trois choix se lisaient comme trois étiquettes, et
       rien ne disait qu'on pouvait en choisir un.)*
     • `multiple`  — sur une bascule, plusieurs choix retenus à la fois (`value` est
       alors un Set, `onChange` en reçoit un) ;
     • `button`    — un bouton à part qui ouvre une autre vue (`text`,
       `active`, `onClick`), au costume de la bascule ;
     • `select`    — quatre choix et plus, ou une dimension qu'on change
       rarement (la granularité, le SUJET du rapport) : le costume de liste de
       la barre de filtre (`lk-filter-select`).

   L'ORDRE DE DÉCLARATION suit la STABILITÉ, pas l'importance : les commandes
   PERMANENTES viennent d'abord, celles qui peuvent DISPARAÎTRE ferment la
   rangée. Comme elle s'aligne à gauche, c'est dans ce sens qu'une commande qui
   s'efface ne déplace plus ses voisines — sans quoi la bascule qu'on utilise
   le plus glisse selon l'écran. *(L'ordre suit l'alignement : il était inverse
   quand la rangée fermait la ligne du titre, à droite.)*

   Le SUJET, quand il existe, se déclare en DERNIER : sa place est tenue, si
   bien qu'ajouter un rapport allonge une liste sans déplacer l'écran. Tant
   qu'aucun n'est déclaré, rien ne s'affiche : un contrôle inerte se cherche
   plus longtemps qu'une place vide.

   La PÉRIODE lue se redit en sous-titre de chaque carte (« · sept mois ») :
   une carte se lit alors isolément, et une capture d'écran garde son contexte.
   C'est ce qui a remplacé la ligne de portée en prose, qui disait la même
   chose une fois pour toutes mais loin des chiffres. */
export default function PageViews({ items = [] }) {
  const shown = (items || []).filter(Boolean);
  if (!shown.length) return null;
  /* Un contrôle déclaré. `afterSlot` le pose APRÈS la place d'accueil
     (2026-09-28 : « Source » du plan de liquidité suit la fenêtre que le plan
     y téléporte). */
  const ctl = (it) => (
    <div className="lk-views__ctl" key={it.id}>
      {it.label && <span className="lk-seg__lbl">{it.label}</span>}
      {it.kind === 'button' ? (
        /* Un BOUTON qui ouvre une autre vue de la page, à part des choix
           exclusifs (2026-10-10 : les Profils des Prévisions, au même niveau
           que l'étape mais séparés d'elle). Le costume de la bascule, un seul
           choix, enfoncé quand la vue est ouverte. */
        <SegmentedToggle
          size="lg"
          ariaLabel={it.text || it.id}
          value={it.active ? 'on' : null}
          onChange={() => it.onClick && it.onClick()}
          options={[{ value: 'on', label: it.text }]}
        />
      ) : it.kind === 'select' ? (
        <select
          className="lk-filter-select"
          aria-label={it.label || it.id}
          value={it.value}
          onChange={(e) => it.onChange(e.target.value)}
        >
          {it.options.map((o) => (
            <option key={o.id} value={o.id} disabled={o.disabled}>{o.label}</option>
          ))}
        </select>
      ) : (
        <SegmentedToggle
          size="lg"
          ariaLabel={it.label || it.id}
          value={it.value}
          multiple={!!it.multiple}
          onChange={it.onChange}
          options={it.options.map((o) => ({
            value: o.id, label: o.label, disabled: o.disabled, title: o.hint,
          }))}
        />
      )}
    </div>
  );
  return (
    <div className="lk-views">
      {(
        <div className="lk-views__row">
          {shown.filter((it) => !it.afterSlot).map(ctl)}
          {/* PLACE D'ACCUEIL, en fin de rangée (2026-09-18) : une CARTE qui
              calcule ses propres choix y téléporte sa commande, au lieu de la
              garder dans son entête. Le plan de liquidité s'en sert pour la
              fenêtre de son graphique, dont les valeurs dépendent des
              exercices qui portent un prévisionnel : les faire remonter à la
              section demanderait de recopier ce calcul. Vide, la place ne
              prend aucun espace. */}
          <div className="lk-views__slot" id="lk-views-slot" />
          {shown.filter((it) => it.afterSlot).map(ctl)}
          {/* SECONDE PLACE, À DROITE (demande d'Olivier, 2026-09-26) : ce qui
              RÈGLE la source (connexions, hypothèses) tenait une rangée à soi,
              sous les vues. Deux natures, deux bouts de la MÊME ligne : les
              vues à gauche, les réglages à droite. Vide, elle ne prend rien. */}
          <div className="lk-views__right" id="lk-views-right" />
        </div>
      )}
    </div>
  );
}
